import React from 'react';
import { useTranslation } from 'react-i18next';
import { useProviderWithoutContext, useUserWithoutContext } from '~/Hooks';
import { Button, Typography, ProfilePictureCard } from '../../../UIKit';
import { makeStyles } from '~/UIKit/Style';

export interface IOnboardingVerificationProps {
  onResendEmail: (uuid: string) => void;
  isLoading?: boolean;
}

const useStyles = makeStyles(() => ({
  onboardingVerificationContainer: {
    width: 300,
    margin: 'auto',
  },
  onboardingVerificationTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    lineHeight: '27px',
    textAlign: 'center',
    overflowWrap: 'break-word',
    marginBottom: 30,
  },
  onboardingVerificationAccount: {
    justifyContent: 'center',
    display: 'flex',
    marginBottom: 30,
  },
  onboardingVerificationDesc: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    lineHeight: '27px',
    wordBreak: 'break-word',
  },
  onboardingVerificationConfirmButton: {
    width: 300,
    height: 50,
    fontSize: '1rem',
  },
}));

export const OnboardingVerification = ({
  onResendEmail,
  isLoading,
}: IOnboardingVerificationProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { currentProvider } = useProviderWithoutContext();
  const { currentUser } = useUserWithoutContext();

  const handleResendEmail = () => {
    if (currentUser?.uuid) {
      onResendEmail(currentUser.uuid);
    }
  };

  return (
    <div className={classes.onboardingVerificationContainer} data-testid="verification-component">
      <Typography className={classes.onboardingVerificationTitle}>
        {t('onboarding.email.confirm')}
      </Typography>
      <Typography className={classes.onboardingVerificationDesc}>
        {t('onboarding.settingup.done')}
      </Typography>
      {currentProvider?.name && (
        <div className={classes.onboardingVerificationAccount}>
          <ProfilePictureCard displayName={currentProvider?.name} fontSize={16} imageSize={30} />
        </div>
      )}
      <Typography className={classes.onboardingVerificationDesc}>
        {t('onboarding.email.confirm.details', { email: currentProvider?.email })}
      </Typography>
      {currentUser?.uuid && (
        <>
          <Typography className={classes.onboardingVerificationDesc}>
            {t('onboarding.email.not_received')}
          </Typography>
          <Button
            className={classes.onboardingVerificationConfirmButton}
            onClick={handleResendEmail}
            variant="contained"
            isLoading={isLoading}
            withGracefulDelay
            color="primary"
            data-testid="resend-email-button"
          >
            {t('onboarding.email.resend')}
          </Button>
        </>
      )}
    </div>
  );
};
